import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ButtonModule } from 'primeng/button';
import { SplitButtonModule } from 'primeng/splitbutton';
import { SurveyModule } from 'survey-angular-ui';
import { Model as SurveyModel } from 'survey-core';
import { TranslateModule } from '@ngx-translate/core';
import { DownloadService } from '../../global-services/download.service';

export interface FormResponseViewerData {
  formDefinition: any; // The Survey.js form definition
  responseData: any; // The response data to display
  formName?: string; // Optional form name for the header
  assignmentId?: string; // Optional assignment ID for download filename
  // Patient information
  patientId?: string; // Optional patient ID
  patientName?: string; // Optional patient name
  // Form metadata
  formId?: string; // Optional form ID
  formIdentifier?: string; // Optional form identifier
  formVersion?: number; // Optional form version
  // Completion information
  completedAt?: string; // Optional completion timestamp
}

@Component({
  selector: 'app-form-response-viewer',
  standalone: true,
  imports: [CommonModule, DialogModule, ProgressSpinnerModule, ButtonModule, SplitButtonModule, SurveyModule, TranslateModule],
  templateUrl: './form-response-viewer.component.html',
  styleUrl: './form-response-viewer.component.css',
})
export class FormResponseViewerComponent implements OnChanges, AfterViewInit {
  @Input() visible = false;
  @Input() data?: FormResponseViewerData;
  @Input() showDownloadButton = true;
  @Input() showCloseButton = true;
  @Input() modalWidth = '90vw';
  @Input() modalHeight = '80vh';
  @Input() modalMaxWidth = '1200px';
  @Input() zIndex?: number;
  @Input() appendTo?: string;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() close = new EventEmitter<void>();

  public surveyModel?: SurveyModel;
  public isLoading = false;
  public errorMessage = '';

  // Download menu items for split button
  public downloadMenuItems = [
    {
      label: 'Download as JSON',
      icon: 'pi pi-file-o',
      command: () => this.downloadResponseAsJson(),
    },
  ];

  constructor(private readonly downloadService: DownloadService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data) {
      this.loadSurveyModel();
    }
  }

  private loadSurveyModel(): void {
    if (!this.data) {
      this.surveyModel = undefined;
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    try {
      // Parse the form definition if it's a string
      const formDefinition = typeof this.data.formDefinition === 'string' ? JSON.parse(this.data.formDefinition) : this.data.formDefinition;

      // Parse the response data if it's a string
      const responseData = typeof this.data.responseData === 'string' ? JSON.parse(this.data.responseData) : this.data.responseData;

      if (!formDefinition) {
        throw new Error('Form definition is required');
      }

      // Create a new survey model with the form definition
      this.surveyModel = new SurveyModel(formDefinition);

      // Set the survey to read-only mode
      this.surveyModel.mode = 'display';

      // Populate the survey with response data (if available)
      if (responseData) {
        this.surveyModel.data = responseData;
      }

      // Apply custom styling after survey is rendered
      setTimeout(() => this.applyAnsweredQuestionStyling(), 100);
    } catch (error) {
      console.error('Error creating survey model:', error);
      this.errorMessage = 'Failed to load form response data. Please check the form definition and response data.';
      this.surveyModel = undefined;
    } finally {
      this.isLoading = false;
    }
  }

  ngAfterViewInit(): void {
    // Apply styling when view is initialized
    if (this.surveyModel) {
      setTimeout(() => this.applyAnsweredQuestionStyling(), 200);
    }
  }

  private applyAnsweredQuestionStyling(): void {
    // Find all survey questions in the DOM
    const questions = document.querySelectorAll('.sd-question');

    questions.forEach((question, index) => {
      const questionElement = question as HTMLElement;
      const hasAnswer = this.questionHasAnswer(questionElement);
      const content = question.querySelector('.sd-question__content') as HTMLElement;
      const title = question.querySelector('.sd-question__title') as HTMLElement;

      // Additional check: use survey model data to verify if question has answer
      let questionName = questionElement.getAttribute('data-name') || questionElement.querySelector('[data-name]')?.getAttribute('data-name');

      // If we can't find the name from DOM, try to get it from the survey model
      if (!questionName && this.surveyModel && this.surveyModel.getAllQuestions) {
        const allQuestions = this.surveyModel.getAllQuestions();
        if (allQuestions[index]) {
          questionName = allQuestions[index].name;
        }
      }

      let hasAnswerFromData = false;
      if (questionName && this.data?.responseData) {
        const response = this.data.responseData[questionName];
        hasAnswerFromData = response !== undefined && response !== null && response !== '';
        // For arrays (multi-select), check if not empty
        if (Array.isArray(response)) {
          hasAnswerFromData = response.length > 0;
        }
      }

      const finalHasAnswer = hasAnswer || hasAnswerFromData;

      if (finalHasAnswer && content && title) {
        // Apply answered question styling
        content.style.background = 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)';
        content.style.border = '1px solid #e0f2fe';
        content.style.borderLeft = '4px solid #0ea5e9';
        content.style.padding = '1rem';
        content.style.borderRadius = '6px';
        content.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';

        title.style.color = '#0c4a6e';
        title.style.fontWeight = '600';
      } else if (content && title) {
        // Apply default styling for unanswered questions
        content.style.background = '#f9fafb';
        content.style.border = '1px solid #e5e7eb';
        content.style.borderLeft = '4px solid #d1d5db';
        content.style.padding = '1rem';
        content.style.borderRadius = '6px';

        title.style.color = '#6b7280';
      }
    });
  }

  private questionHasAnswer(questionElement: HTMLElement): boolean {
    // Check for various types of answered inputs

    // Text inputs with values (including hidden inputs that might contain values)
    const textInputs = questionElement.querySelectorAll('input[type="text"], input[type="email"], input[type="number"], input[type="hidden"], textarea');
    for (let i = 0; i < textInputs.length; i++) {
      const input = textInputs[i] as HTMLInputElement;
      if (input.value && input.value.trim() !== '') {
        return true;
      }
    }

    // Radio buttons and checkboxes that are checked
    const checkedInputs = questionElement.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked');
    if (checkedInputs.length > 0) {
      return true;
    }

    // Select dropdowns with selected values
    const selects = questionElement.querySelectorAll('select');
    for (let i = 0; i < selects.length; i++) {
      const select = selects[i] as HTMLSelectElement;
      if (select.value && select.value !== '' && select.value !== select.options[0]?.value) {
        return true;
      }
    }

    // Look for Survey.js specific classes that indicate selection
    const selectedElements = questionElement.querySelectorAll('.sd-selectbase__item--checked, .sd-rating__item--selected, .sd-boolean--checked');
    if (selectedElements.length > 0) {
      return true;
    }

    // Look for file previews
    const filePreviews = questionElement.querySelectorAll('.sd-file__preview');
    if (filePreviews.length > 0) {
      return true;
    }

    // Check for any element with text content that might indicate an answer
    const answerElements = questionElement.querySelectorAll('.sd-input, .sd-comment, .sd-text');
    for (let i = 0; i < answerElements.length; i++) {
      const element = answerElements[i] as HTMLElement;
      if (element.textContent && element.textContent.trim() !== '') {
        return true;
      }
    }

    // Check for visible selected options in various question types
    const visibleSelections = questionElement.querySelectorAll('[class*="selected"], [class*="checked"], [class*="active"]');
    if (visibleSelections.length > 0) {
      return true;
    }

    return false;
  }

  public onVisibleChange(visible: boolean): void {
    this.visibleChange.emit(visible);
    if (!visible) {
      this.onClose();
    }
  }

  public onClose(): void {
    this.close.emit();
  }

  public onDownloadResponse(): void {
    // Default to CSV download for main button
    this.downloadResponseAsCsv();
  }

  public downloadResponseAsJson(): void {
    if (this.data?.responseData) {
      const filename = this.generateFilename('json');
      this.downloadService.downloadAsJson({
        data: this.data.responseData,
        filename,
      });
    }
  }

  public downloadResponseAsCsv(): void {
    if (this.data?.responseData) {
      const filename = this.generateFilename('csv');
      this.downloadService.downloadAsCsv({
        data: this.data.responseData,
        filename,
      });
    }
  }

  private generateFilename(extension: string): string {
    const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

    if (this.data?.assignmentId) {
      return `form-responses-${this.data.assignmentId}.${extension}`;
    } else if (this.data?.formIdentifier && this.data?.patientName) {
      const sanitizedPatientName = this.data.patientName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
      return `${this.data.formIdentifier}-${sanitizedPatientName}-${timestamp}.${extension}`;
    } else if (this.data?.formName) {
      const sanitizedFormName = this.data.formName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
      return `${sanitizedFormName}-${timestamp}.${extension}`;
    } else {
      return `form-responses-${timestamp}.${extension}`;
    }
  }

  public getModalStyle(): any {
    return {
      width: this.modalWidth,
      maxWidth: this.modalMaxWidth,
      height: this.modalHeight,
      'z-index': this.zIndex !== undefined ? this.zIndex : 1000, // Default z-index if not provided
    };
  }

  public getHeaderTitle(): string {
    if (this.data?.formName) {
      return `Form Response - ${this.data.formName}`;
    }
    return 'Form Response';
  }

  public hasMetadata(): boolean {
    if (!this.data) return false;
    return !!(this.data.patientId || this.data.patientName || this.data.formId || this.data.formIdentifier || this.data.formVersion || this.data.completedAt);
  }

  public formatCompletedAt(completedAt: string): string {
    if (!completedAt) return '';

    try {
      const date = new Date(completedAt);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return completedAt; // Return as-is if parsing fails
    }
  }
}
